import { useRef, useEffect, useContext, useState, useMemo } from 'react';
import { BubbleMenu } from '@tiptap/react/menus';
import { List, useMediaQuery, useTheme } from '@mui/material';
import { BubbleMenuProps } from 'components/RichTextInput/types/customToolbarBtnTypes';
import { hasExtension } from 'components/EditorBar/utils';
import { AssetViewContext } from 'screens/editorial/asset/context/AssetViewContextProvider';
import { EditorBubbleMenuProvider, useEditorBubbleMenu } from './context';
import ThreadsBubble from './BubbleMenus/ThreadsBubble/ThreadsBubble';
import { LinkBubble } from './BubbleMenus/LinkBubble/LinkBubble';
import { ThreadsBubbleList } from './BubbleMenus/ThreadsBubble/ThreadsBubbleList';
import { LinkBubbleList } from './BubbleMenus/LinkBubble/LinkBubbleList';
import TableBubbleButtons from './BubbleMenus/TableBubble/TableBubbleButtons';
import { getAiToolkit } from '@tiptap-pro/ai-toolkit';
import AiSuggestBubble from './BubbleMenus/AiSuggestBubble/AiSuggestBubble';

// Type for bubbleMenu storage
declare module '@tiptap/core' {
  interface Storage {
    bubbleMenu?: {
      view: string | boolean;
    };
    isToolbarToggle?: boolean;
  }
}

const EditorBubble = () => {
  const { editor, bubbleCleanup, setBubbleView, bubbleViewState } = useEditorBubbleMenu();
  const theme = useTheme();
  const largeViewport = useMediaQuery(theme.breakpoints.down('lg'));
  const mediumViewport = useMediaQuery(theme.breakpoints.down('md'));
  const context = useContext(AssetViewContext);
  const isRightClickRef = useRef(false);
  const bubbleMenuRef = useRef<HTMLDivElement>(null);
  const lastSelectionRef = useRef<{ from: number; to: number } | null>(null);
  const lastMouseDownPosRef = useRef<{ from: number; to: number } | null>(null);
  const lastLinkHrefRef = useRef<string | null>(null);

  const hasThreads = hasExtension(editor, 'comments');
  const hasLink = hasExtension(editor, 'link');
  const hasTable = hasExtension(editor, 'table');
  const hasAiSuggest = hasExtension(editor, 'aiToolkit');

  // Check if current view is a secondary view (needs positioning delay)
  const isSecondaryView = useMemo(() => {
    return (
      bubbleViewState === 'linkEditor' ||
      bubbleViewState === 'threadsAddComment' ||
      (typeof bubbleViewState === 'string' && bubbleViewState.startsWith('linkPreview'))
    );
  }, [bubbleViewState]);

  // Track which view we've positioned
  const [positionedView, setPositionedView] = useState<string | boolean | null>(null);

  // Reset positioned state when view changes
  useEffect(() => {
    setPositionedView(null);
  }, [bubbleViewState]);

  // Determine if menu should be visible - only show secondary views after they've been positioned
  const isVisible = !isSecondaryView || positionedView === bubbleViewState;

  // Track mouse events for right-click detection
  useEffect(() => {
    if (!editor) return;

    const handleMouseDown = (event: MouseEvent) => {
      // Track selection at mousedown to detect real user selections
      lastMouseDownPosRef.current = {
        from: editor.state.selection.from,
        to: editor.state.selection.to,
      };

      // Detect right-click (button 2)
      if (event.button === 2) {
        isRightClickRef.current = true;
      }
    };

    const handleContextMenu = () => {
      // Reset flag after context menu is handled
      setTimeout(() => {
        isRightClickRef.current = false;
      }, 100);
    };

    if (editor.isDestroyed) return;

    let editorElement: HTMLElement;

    try {
      editorElement = editor.view.dom;
    } catch {
      return;
    }

    editorElement.addEventListener('mousedown', handleMouseDown);
    editorElement.addEventListener('contextmenu', handleContextMenu);

    return () => {
      editorElement.removeEventListener('mousedown', handleMouseDown);
      editorElement.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [editor]);

  // Trigger repositioning when view changes (e.g., switching to link editor or comment bubble)
  // Use ResizeObserver to detect when Floating UI has finished positioning
  useEffect(() => {
    if (!editor || !isSecondaryView || !bubbleMenuRef.current) return;

    let hasResized = false;

    // Observe when the element size changes (content swap) and when it stabilizes (positioned)
    const resizeObserver = new window.ResizeObserver(() => {
      if (!hasResized) {
        hasResized = true;
        // Wait for Floating UI to position and browser to paint
        setTimeout(() => {
          setPositionedView(bubbleViewState);
        }, 50);
      }
    });

    resizeObserver.observe(bubbleMenuRef.current);

    // Trigger Floating UI recalculation
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });

    return () => {
      resizeObserver.disconnect();
    };
  }, [editor, isSecondaryView, bubbleViewState]);

  // Reset link href ref when bubble menu closes or switches away from link preview
  useEffect(() => {
    if (
      bubbleViewState === false ||
      (typeof bubbleViewState === 'string' && !bubbleViewState.startsWith('linkPreview'))
    ) {
      lastLinkHrefRef.current = null;
    }
  }, [bubbleViewState]);

  // No editor available
  if (!editor) {
    return null;
  }

  // Editor disabled - hide bubble menu
  if (!editor.isEditable) {
    return null;
  }

  return (
    <BubbleMenu
      data-testid="editor-bubble-menu"
      pluginKey="bubbleMenuLink"
      className="bubble-menu-dark"
      updateDelay={0}
      editor={editor}
      getReferencedVirtualElement={() => {
        const { state, view } = editor;
        const { selection } = state;
        const { $anchor } = selection;

        // Handle link positioning - position below the entire link element
        if (editor.isActive('link')) {
          const { from, to } = selection;

          // Get all DOM nodes that contain the link
          const linkNodes: HTMLElement[] = [];
          let currentPos = from;

          while (currentPos <= to) {
            const domNode = view.domAtPos(currentPos);
            if (domNode.node) {
              // Find the closest link element
              let linkElement: HTMLElement | null = null;
              if (domNode.node.nodeType === Node.ELEMENT_NODE) {
                linkElement = (domNode.node as HTMLElement).closest('a');
              } else if (domNode.node.parentElement) {
                linkElement = domNode.node.parentElement.closest('a');
              }

              if (linkElement && !linkNodes.includes(linkElement)) {
                linkNodes.push(linkElement);
              }
            }
            currentPos++;
          }

          // If we found link elements, calculate bounding rect that encompasses all of them
          if (linkNodes.length > 0) {
            const rects = linkNodes.map((node) => node.getBoundingClientRect());
            const left = Math.min(...rects.map((r) => r.left));
            const right = Math.max(...rects.map((r) => r.right));
            const bottom = Math.max(...rects.map((r) => r.bottom));

            // Position menu at the bottom of the link element
            return {
              getBoundingClientRect: () => new DOMRect(left, bottom, right - left, 0),
            };
          }
        }

        // Handle table positioning - position menu at top-left of table
        if (editor.isActive('table')) {
          // Walk up from the current position to find the table node
          let depth = $anchor.depth;
          while (depth > 0) {
            const node = $anchor.node(depth);
            if (node.type.name === 'table') {
              const pos = $anchor.before(depth);
              const domNode = view.nodeDOM(pos);

              let tableElement: HTMLElement | null = null;
              if (domNode instanceof HTMLElement) {
                if (domNode.tagName === 'TABLE') {
                  tableElement = domNode;
                } else {
                  tableElement = domNode.querySelector('table');
                }
              }

              if (tableElement) {
                const rect = tableElement.getBoundingClientRect();
                // Position above the table at top-left
                return {
                  getBoundingClientRect: () =>
                    new DOMRect(rect.left, rect.top - 55, rect.width, 0),
                };
              }
              break;
            }
            depth--;
          }
        }

        return null;
      }}
      options={{
        placement: 'bottom-start',
        strategy: 'absolute',
        flip: false,
        shift: false,
        hide: false,
        autoPlacement: false,
      }}
      style={{
        zIndex:
          bubbleViewState === 'linkEditor' ||
          bubbleViewState === 'threadsAddComment' ||
          (typeof bubbleViewState === 'string' &&
            bubbleViewState.startsWith('linkPreview'))
            ? 1100
            : 999,
      }}
      shouldShow={({ editor }) => {
        let shouldShow = false;

        // Don't show bubble menu on right-click (context menu)
        if (isRightClickRef.current) {
          bubbleCleanup();
          return false;
        }

        if (!editor.storage.bubbleMenu) {
          bubbleCleanup();
          return false;
        }

        const currentView = editor.storage.bubbleMenu?.view;
        const { from, to } = editor.state.selection;

        // Check if this is a toolbar-initiated toggle (ignore these changes)
        const isToolbarToggle = editor.storage.isToolbarToggle === true;

        // Check if current selection differs from where the mouse was pressed
        // If different AND not a toolbar toggle, it's a new user selection
        const isUserSelectionChange =
          !isToolbarToggle &&
          lastMouseDownPosRef.current &&
          (lastMouseDownPosRef.current.from !== from ||
            lastMouseDownPosRef.current.to !== to);

        // If user made a new selection and we're in an explicit view (linkEditor/threadsEditor),
        // check if they clicked on a link (show preview) or regular text (show bubbleMenu)
        if (
          isUserSelectionChange &&
          (currentView === 'linkEditor' || currentView === 'threadsEditor')
        ) {
          if (editor.isActive('link')) {
            // Clicked on another link - show its preview
            const linkView = `linkPreview-${editor.state.selection.$anchor.pos}`;
            editor.storage.bubbleMenu = { view: linkView };
            setBubbleView(linkView);
            lastLinkHrefRef.current = editor.getAttributes('link').href;
            shouldShow = true;
          } else if (!editor.state.selection.empty) {
            // Clicked on regular text with selection - show bubble menu
            lastSelectionRef.current = { from, to };
            editor.storage.bubbleMenu = { view: 'bubbleMenu' };
            setBubbleView('bubbleMenu');
            shouldShow = true;
          }
        } else if (currentView === 'linkEditor' || currentView === 'threadsEditor') {
          // Close if selection is empty AND it's not a toolbar toggle
          if (editor.state.selection.empty && !isToolbarToggle) {
            bubbleCleanup();
            return false;
          }
          // Keep explicit view (toolbar toggle or text still selected)
          lastSelectionRef.current = { from, to };
          shouldShow = true;
        } else if (editor.isActive('link') && currentView !== 'linkEditor') {
          // Get current link href
          const currentHref = editor.getAttributes('link').href;

          // If already showing a linkPreview, check if href changed
          if (typeof currentView === 'string' && currentView.startsWith('linkPreview')) {
            // If href changed, show new link preview (force remount)
            if (currentHref !== lastLinkHrefRef.current) {
              const linkView = `linkPreview-${editor.state.selection.$anchor.pos}`;
              editor.storage.bubbleMenu = { view: linkView };
              setBubbleView(linkView);
              lastLinkHrefRef.current = currentHref;
            }
            shouldShow = true;
          } else {
            // Show new link preview
            const linkView = `linkPreview-${editor.state.selection.$anchor.pos}`;
            editor.storage.bubbleMenu = { view: linkView };
            setBubbleView(linkView);
            lastLinkHrefRef.current = currentHref;
            shouldShow = true;
          }
        } else if (editor.isActive('table')) {
          shouldShow = true;
        } else if (
          !editor.state.selection.empty &&
          !editor.isActive('bloxInline') &&
          !editor.isActive('horizontalRule')
        ) {
          lastSelectionRef.current = { from, to };
          editor.storage.bubbleMenu = { view: 'bubbleMenu' };
          setBubbleView('bubbleMenu');
          shouldShow = true;
        }

        if (hasAiSuggest) {
          let aiToolkit = getAiToolkit(editor);

          const selectedSuggestion = aiToolkit.getSelectedSuggestion();
          if (selectedSuggestion !== null) {
            setBubbleView('aiSuggestBubble');
            shouldShow = true;
          }
        }

        if (shouldShow) {
          if (context) {
            if (largeViewport) {
              context.setRightDrawerOpen(false);
            }

            if (mediumViewport) {
              context.setLeftDrawerOpen(false);
            }
          }
          return true;
        }

        bubbleCleanup();
        return false;
      }}
    >
      <div
        ref={bubbleMenuRef}
        style={{
          visibility: isVisible ? 'visible' : 'hidden',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'none' : 'translate(-10000px, -10000px)',
          pointerEvents: isVisible ? 'auto' : 'none',
          transition: isVisible ? 'opacity 0.15s ease-in-out' : 'none',
        }}
      >
        {hasTable && editor.isActive('table') ? (
          <TableBubbleButtons />
        ) : (
          <>
            {bubbleViewState === 'bubbleMenu' && (
              <List
                data-testid="bubble-menu-list"
                sx={(theme) => ({
                  width: 'fit-content',
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: '8px',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  zIndex: 1,
                  position: 'relative',
                  '& .MuiListItem-root': {
                    backgroundColor: theme.palette.background.paper,
                    '& .MuiButtonBase-root': {
                      gap: '8px',
                    },
                    '& .MuiListItemIcon-root': {
                      minWidth: 'unset',
                    },
                    '&:first-of-type': {
                      '& .MuiButtonBase-root': {
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                      },
                    },
                    '&:last-of-type': {
                      '& .MuiButtonBase-root': {
                        borderBottomLeftRadius: '8px',
                        borderBottomRightRadius: '8px',
                      },
                    },
                  },
                  p: 0,
                })}
              >
                {hasThreads && <ThreadsBubbleList />}
                {hasLink && <LinkBubbleList />}
              </List>
            )}
            {hasAiSuggest && <AiSuggestBubble />}
            {hasThreads && <ThreadsBubble />}
            {hasLink && <LinkBubble />}
          </>
        )}
      </div>
    </BubbleMenu>
  );
};

const EditorBubbleMenu = ({ editor }: BubbleMenuProps) => {
  if (!editor) {
    return null;
  }

  return (
    <EditorBubbleMenuProvider editor={editor}>
      <EditorBubble />
    </EditorBubbleMenuProvider>
  );
};

export default EditorBubbleMenu;
